import { Formik } from "formik";
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import {
  Col,
  Icon,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  notification,
} from "antd";
import NumberFormat from "react-number-format";
import {
  createNewFinancialClass,
  updateFinancialClass,
} from "../../../services/financialClassService";
import { findAllFinancialNaturesByGroupIdAndFarmId } from "../../../services/financialNatureService";
import ButtonStandard from "../../utils/button";
import InfoTooltip from "../../utils/infoTooltip";
import { Container, Footer } from "./styles";

const validationSchema = Yup.object({
  financialNatureId: Yup.string().required(),
  name: Yup.string().trim().required(),
  shouldCost: Yup.boolean().required(),
  shouldDisbursement: Yup.boolean().required(),
  depreciation: Yup.number()
    .notRequired()
    .when(["shouldCost"], {
      is: (shouldCost) => shouldCost === true,
      then: Yup.number().min(1).max(9999).required(),
      otherwise: Yup.number().notRequired().nullable(),
    }),
  costCenterName: Yup.string()
    .notRequired()
    .when(["id"], {
      is: (id) => id === null,
      then: Yup.string().required(),
      otherwise: Yup.string().nullable().notRequired(),
    }),
});

const INITIAL_FORM_STATE = {
  id: null,
  name: null,
  status: "Active",
  financialNatureId: null,
  depreciation: null,
  shouldCost: true,
  shouldDisbursement: true,
  costCenterName: null,
  externalIntegrationCode: null,
};

const DrawerFinancialClass = ({
  isDrawerFinancialClassVisible = false,
  data,
  handleCloseDrawer,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [listFinancialNature, setListFinancialNature] = useState([]);
  const [isLoadingFinancialNature, setIsLoadingFinancialNature] =
    useState(false);

  const formRef = useRef(null);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchFinancialNature = useCallback(async () => {
    setIsLoadingFinancialNature(true);
    try {
      const {
        data: { results: natures },
      } = await findAllFinancialNaturesByGroupIdAndFarmId({
        groupId,
        farmId,
      });
      setListFinancialNature(natures);
    } catch (error) {
    } finally {
      setIsLoadingFinancialNature(false);
    }
  }, [farmId, groupId]);

  function handleSubmitForm(values) {
    const title = translation.financial.financialClass.messages.warningTitle;
    const content = (
      <ul>
        <li>
          {translation.financial.financialClass.messages.warningContent}
        </li>
      </ul>
    );
    handleSaveClass(values);
    notification.warning({
      message: title,
      description: content,
      duration: 6,
    });
  }
  async function handleSaveClass(values) {
    setIsLoading(true);
    try {
      if (values.id === null) {
        await createNewFinancialClass({
          groupId,
          farmId,
          body: values,
        });
        notification.success({
          message: translation.financial.financialClass.messages.saveSuccess,
        });
      } else {
        await updateFinancialClass({
          groupId,
          farmId,
          id: values.id,
          body: values,
        });
        notification.success({
          message: translation.financial.financialClass.messages.updateSuccess,
        });
      }
      handleCloseDrawer();
    } catch (error) {
      notification.error({
        message: translation.financial.financialClass.messages.saveError,
      });
    } finally {
      setIsLoading(false);
    }
  }
  function handleDrawerVisible(visible) {
    if (visible) {
      fetchFinancialNature();
      formRef.current.resetForm(data ? data : INITIAL_FORM_STATE);
    } else {
      formRef.current.resetForm(INITIAL_FORM_STATE);
    }
  }

  return (
    <Container
      title={
        data?.id == null
          ? translation.financialClass.form.titleCreateNew
          : translation.financialClass.form.titleEdit
      }
      width={700}
      onClose={handleCloseDrawer}
      maskClosable={false}
      visible={isDrawerFinancialClassVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Spin spinning={isLoading}>
        <Formik
          ref={formRef}
          enableReinitialize={true}
          initialValues={data}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
          render={({
            values,
            errors,
            submitCount,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="drawerForm">
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}
                {values.id === null && (
                  <>
                    {/* Financial Nature */}
                    <Row type="flex" className="rowLabel">
                      <label
                        className={
                          errors.financialNatureId && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.financialClass.form.financialNatureId}*
                      </label>
                    </Row>
                    <Row type="flex">
                      <Select
                        name="financialNatureId"
                        value={values.financialNatureId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        loading={isLoadingFinancialNature}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => {
                          setFieldValue("financialNatureId", value);
                        }}
                      >
                        {listFinancialNature.map((l) => (
                          <Select.Option key={l.id} value={l.id}>
                            {l.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </>
                )}
                {/* Name */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.name && submitCount > 0 ? "error" : ""}
                  >
                    {translation.financialClass.form.name}*
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="name"
                    value={values.name}
                    maxLength={50}
                    disabled={!values.groupId && !values.farmId}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    onChange={(e) => {
                      setFieldValue("name", e.target.value);
                    }}
                  />
                </Row>
                {/* ShouldCost */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.shouldCost && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.financialClass.form.shouldCost}*{" "}
                    <InfoTooltip title="Selecione se as movimentações financeiras, associadas a esta Classe, deverão ser consideradas para compor o custo de produção dos animais. " />
                  </label>
                </Row>
                <Row type="flex">
                  <Radio.Group
                    value={values.shouldCost}
                    onChange={(e) => {
                      setFieldValue("shouldCost", e.target.value);
                      if (e.target.value === false) {
                        setFieldValue("depreciation", null);
                      }
                    }}
                  >
                    <Radio style={{ color: "#4f4f4f" }} value={true}>
                      {translation.buttons.yes}
                    </Radio>
                    <Radio style={{ color: "#4f4f4f" }} value={false}>
                      {translation.buttons.no}
                    </Radio>
                  </Radio.Group>
                </Row>
                {/* shouldDisbursement */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.shouldDisbursement && submitCount > 0
                        ? "error"
                        : ""
                    }
                  >
                    {translation.financialClass.form.shouldDisbursement}*{" "}
                    <InfoTooltip title="Selecione se as movimentações financeiras, associadas a esta Classe, deverão ser consideradas para compor o custo de desembolso dos animais. " />
                  </label>
                </Row>
                <Row type="flex">
                  <Radio.Group
                    value={values.shouldDisbursement}
                    onChange={(e) => {
                      setFieldValue("shouldDisbursement", e.target.value);
                    }}
                  >
                    <Radio style={{ color: "#4f4f4f" }} value={true}>
                      {translation.buttons.yes}
                    </Radio>
                    <Radio style={{ color: "#4f4f4f" }} value={false}>
                      {translation.buttons.no}
                    </Radio>
                  </Radio.Group>
                </Row>
                {/* Depreciation */}
                {values.shouldCost && (
                  <>
                    <Row type="flex" className="rowLabel">
                      <label
                        className={
                          errors.depreciation && submitCount > 0 ? "error" : ""
                        }
                      >
                        {translation.financialClass.form.depreciation}*
                      </label>
                    </Row>
                    <Row type="flex">
                      <Col span={8}>
                        <NumberFormat
                          customInput={Input}
                          value={values.depreciation}
                          placeholder={translation.defaultPlaceholder}
                          decimalScale={0}
                          allowNegative={false}
                          max={9999}
                          addonAfter="meses"
                          onValueChange={({ floatValue }) =>
                            setFieldValue(
                              "depreciation",
                              floatValue
                                ? floatValue > 9999
                                  ? 9999
                                  : floatValue
                                : null
                            )
                          }
                        />
                      </Col>
                    </Row>
                  </>
                )}
                {values.id === null && (
                  <>
                    {/* CostCenterName */}
                    <Row type="flex" className="rowLabel">
                      <label
                        className={
                          errors.costCenterName && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.financialClass.form.costCenterName}*
                      </label>
                    </Row>
                    <Row type="flex">
                      <Select
                        name="costCenterName"
                        value={values.costCenterName || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => {
                          setFieldValue("costCenterName", value);
                        }}
                      >
                        <Select.Option value="PRODUCAO_ANIMAL">
                          Produção Animal
                        </Select.Option>
                        <Select.Option value="UTILIDADES">
                          Utilidades
                        </Select.Option>
                        <Select.Option value="INFRAEST_PRODUTIVA">
                          Infraest. Produtiva
                        </Select.Option>
                        <Select.Option value="INFRAEST_OPERACIONAL">
                          Infraest. Operacional
                        </Select.Option>
                        <Select.Option value="INFRAEST_APOIO">
                          Infraest. Apoio
                        </Select.Option>
                        <Select.Option value="TAXA_IMPOSTOS">
                          Taxas & Impostos
                        </Select.Option>
                        <Select.Option value="SERVICOS">Serviços</Select.Option>
                        <Select.Option value="INVSUMOS_OPERACIONAIS">
                          Insumos Operacionais
                        </Select.Option>
                        <Select.Option value="PRODUCAO_PASTO_AGRICOLA">
                          Produção Pasto/Agrícola
                        </Select.Option>
                        <Select.Option value="OUTRAS_DESPESAS">
                          Outras Despesas
                        </Select.Option>
                        <Select.Option value="OUTRAS_RECEITAS">
                          Outras Receitas
                        </Select.Option>
                        <Select.Option value="VENDA_DA_PRODUCAO">
                          Venda da Produção
                        </Select.Option>
                      </Select>
                    </Row>
                  </>
                )}
                {/* External Code */}
                <Row type="flex" className="rowLabel" style={{ marginTop: 16 }}>
                  <label
                    className={
                      errors.externalIntegrationCode && submitCount > 0
                        ? "error"
                        : ""
                    }
                  >
                    {translation.financialClass.form.externalIntegrationCode}
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="externalIntegrationCode"
                    value={values.externalIntegrationCode || ""}
                    maxLength={20}
                    placeholder={translation.defaultPlaceholder}
                    onChange={(e) =>
                      setFieldValue("externalIntegrationCode", e.target.value)
                    }
                  />
                </Row>
              </div>
              <Footer>
                <Row type="flex">
                  <Col span={24} className="buttonsDiv">
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={handleCloseDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>

                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default DrawerFinancialClass;
